<template>
  <transition name="fade" mode="out-in">
    <div v-if="isOpen" class="modal-wrapper document">
      <div class="modal-overlay" @click="emits('close')" />
      <CoverPage
        :candidate="candidate"
        :campaign_name="campaignStore.currentCampaign.name"
      />
      <ResultPage
        class="results-page"
        :pagination="`1/${nb_pages}`"
        :modules="skills_evaluated.filter((elt) => elt.type !== 'style')"
        :candidate="candidate"
      />
      <PositioningPage
        :pagination="`2/${nb_pages}`"
        :modules="skills_evaluated"
        :candidate="candidate"
        :campaign_scores="campaignStore.currentCampaign.averageScores"
      />
      <SkillPage
        v-for="(skill, index) in skills_evaluated.filter(
          (elt) => elt.id !== 'learn_strategy' && elt.id !== 'leadership'
        )"
        :key="skill"
        :index="index"
        :total_pages="nb_pages"
        :result="{
          module: getResultSkillId(skill),
          rank: candidate.results[getResultSkillId(skill)].rank,
          badge_rank: candidate.results[getResultSkillId(skill)].badge_rank,
          score:
            skill.id_v2 === 'pb_solving'
              ? candidate.results[getResultSkillId(skill)].rank === 'A'
                ? 100
                : candidate.results[getResultSkillId(skill)].rank === 'B'
                ? 70
                : candidate.results[getResultSkillId(skill)].rank === 'C'
                ? 30
                : 10
              : candidate.results[getResultSkillId(skill)].score,
        }"
        :appreciation="
          skill.type !== 'style'
            ? getAppreciation(getResultSkillId(skill))
            : undefined
        "
        :social_desire_score="candidate.results.social_desire_score"
      />
      <LearningStrategy
        v-if="campaignStore.currentCampaign.version === 2"
        :index="skills_evaluated.length - 2"
        :total_pages="nb_pages"
        :result="{
          module: getResultSkillId({ id: 'learn_strategy' }),
          id: candidate.results[getResultSkillId({ id: 'learn_strategy' })].value,
        }"
      />
      <LeadershipView
        v-if="campaignStore.currentCampaign.version === 2 && candidate.results[getResultSkillId({ id: 'leadership' })].value"
        :index="skills_evaluated.length - 1"
        :total_pages="nb_pages"
        :result="{
          module: getResultSkillId({ id: 'leadership' }),
          id: candidate.results[getResultSkillId({ id: 'leadership' })].value,
        }"
      />
      <MotivationResult
        v-if="campaignStore.currentCampaign.version === 2"
        :total_pages="nb_pages"
        :index="skills_evaluated.length"
        :motivation-style="candidate.results.motivation_style"
      />
      <BadgesPage
        v-if="campaignStore.currentCampaign.version === 2"
        :pagination="`${nb_pages - 2}/${nb_pages}`"
        :results="candidate.results"
        :modules="skills_evaluated"
        :candidate="candidate"
      />
      <component
        :is="
          campaignStore.currentCampaign.version === 1
            ? CandidatePage
            : CandidatePageV2
        "
        :results="candidate.results"
        :modules="skills_evaluated"
        :pagination="`${nb_pages - 1}/${nb_pages}`"
      />
      <LaboratoryPage :pagination="`${nb_pages}/${nb_pages}`" />
      <LastPage />
      <div class="button-group">
        <Button
          class="secondary sm icon no-dark-mode"
          label="Fermer la prévisualisation"
          icon="bi-x-lg"
          @click="emits('close')"
        />
        <Button
          class="primary sm icon"
          :label="state.download_button_label"
          icon="bi-download"
          @click="generatePDF"
          :loading="state.is_loading"
          :disabled="!state.is_ready"
        />
        <a
          :href="state.document_url"
          :download="report_document_name"
          ref="download_link"
          style="display: none"
        />
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, reactive, computed, watch } from "vue";
import { useCampaignStore } from "@/stores/campaign.js";
import { useModulesStore } from "@/stores/modules.js";
import { text, image, svg } from "@pdfme/schemas";
import { generate } from "@pdfme/generator";
import { PDFDocument } from "pdf-lib";
import { getWording, getDate } from "@/composables/utils.js";
import PositioningPage from "@/components/pdf/partials/Positioning.vue";
import CandidatePage from "@/components/pdf/partials/Candidate.vue";
import CandidatePageV2 from "@/components/pdf/partials/CandidateV2.vue";
import LaboratoryPage from "@/components/pdf/partials/Laboratory.vue";
import ResultPage from "@/components/pdf/partials/Result.vue";
import SkillPage from "@/components/pdf/partials/Skill.vue";
import BadgesPage from "@/components/pdf/partials/Badges.vue";
import CoverPage from "@/components/pdf/partials/Cover.vue";
import LastPage from "@/components/pdf/partials/Last.vue";
import html2canvas from "html2canvas";
import {
  Start,
  Adaptability,
  StressManage,
  PbSolving,
  ActiveListen,
  ActiveListenHighSocialDesirability,
  AutoEfficient,
  AutoEfficientHighSocialDesirability,
  Planning,
  Decisions,
  Empathy,
  EmpathyHighSocialDesirability,
  TimeManage,
  LearnStrategy,
  Leadership,
  Motivation,
  Badges,
  End,
} from "@/assets/pdf/pdf_document.js";
import LearningStrategy from "@/components/pdf/partials/LearningStrategy.vue";
import LeadershipView from "@/components/pdf/partials/Leadership.vue";
import MotivationResult from "@/components/pdf/partials/MotivationResult.vue";

const emits = defineEmits(["close"]);

const campaignStore = useCampaignStore();

const { modules } = useModulesStore();

const download_link = ref(null);

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  candidate: {
    type: Object,
    required: true,
  },
});

const state = reactive({
  is_loading: false,
  document_url: null,
  inputs: null,
  fonts: null,
  is_ready: false,
  download_button_label: "Télécharger le rapport",
});

/*
{
    "adaptability_score": {
        "rank": "A",
        "note": 100,
        "score": 100
    },
    "stress_manage_score": {
        "rank": "A",
        "note": 81.55,
        "score": 81.55
    },
    "time_manage_score": {
        "rank": "B",
        "note": 76.81,
        "score": 76.81
    },
    "auto_efficient_score": {
        "rank": "A",
        "note": 80,
        "score": 80
    },
    "active_listen_score": {
        "rank": "A",
        "note": 85.45,
        "score": 85.45
    },
    "empathy_score": {
        "rank": "A",
        "note": 83.33,
        "score": 83.33
    },
    "social_desire_score": {
        "rank": "A",
        "note": 90,
        "score": 90
    },
    "pb_solving_score": {
        "rank": "E",
        "note": 55.56,
        "score": 55.56
    },
    "decisions_score": {
        "rank": "A",
        "note": 99.5,
        "score": 99.5
    },
    "domination_style": "",
    "learn_strategy_style": {
        "rank": "C",
        "value": "Organisation"
    },
    "motivation_style": "Extra",
    "want_to_learn_badge": 1,
    "curious_badge": 1,
    "efficient_badge": 0,
    "autonomous_badge": 1,
    "perseverance_badge": 0,
    "cooperation_badge": 1,
    "diplomate_badge": 0,
    "positive_badge": 0,
    "bold_badge": 0,
    "altruist_badge": 0,
    "pragmatist_badge": 1,
    "optimist_badge": 0,
    "humour_badge": 1,
    "innovation_badge": 0,
    "creation_time": "2025-01-28 14:01:27",
    "compatibility": 82.77499999999999
}
 */

const skills_evaluated = computed(() => {
  if (!props.candidate.results) return [];

  return modules.filter((elt) => {
    if (campaignStore.currentCampaign.version === 1) {
      return Object.keys(props.candidate.results).includes(elt.id);
    }

    if (elt.id === "motivation") return false;
    if (elt.disabled) return false;

    if (elt.id === "leadership") return campaignStore.isV2_1(campaignStore.currentCampaign);
    if (elt.id === "planning") return campaignStore.isV2_1(campaignStore.currentCampaign);

    return true;
  });
});

// Number of pages doesn't include cover and last page
const nb_pages = computed(() => {
  // In version 1, we have 10 pages total
  if (campaignStore.currentCampaign.version === 1)
    return 1 // profile
      + 1 // graph
      + skills_evaluated.value.length * 2 // 2 pages per skill
      + 1 // extract
      + 1; // science

  // In version 2, we have 25 pages total
  return 1 // profile
    + 1 // graph
    + skills_evaluated.value.length * 2 // 2 pages per skill
    + 2 // motivation
    + 1 // badges 
    + 1 // extract
    + 1; // science
});

const report_document_name = computed(() => {
  const campaignName = campaignStore.currentCampaign.name
    .split(" ")
    .map((elt) => elt.charAt(0).toUpperCase() + elt.slice(1))
    .join("_");

  return `${props.candidate.candidate.first_name}_${props.candidate.candidate.last_name}_Campagne_${campaignName}.pdf`;
});

async function generatePDF() {
  state.is_loading = true;
  state.is_ready = false;
  state.download_button_label = "Génération du rapport...";

  const plugins = { text, image, svg };

  const { inputs } = state;

  // Load fonts
  const font = {
    bold: {
      data: await fetch("/fonts/BioSans-Bold.ttf").then((res) =>
        res.arrayBuffer()
      ),
    },
    semiBold: {
      data: await fetch("/fonts/BioSans-SemiBold.ttf").then((res) =>
        res.arrayBuffer()
      ),
    },
    regular: {
      data: await fetch("/fonts/BioSans-Regular.ttf").then((res) =>
        res.arrayBuffer()
      ),
      fallback: true,
    },
  };

  // Templates for each skill
  const skills_template = [
    { template: Adaptability, id: "adaptability" },
    { template: StressManage, id: "stress_manage" },
    { template: PbSolving, id: "pb_solving" },
    {
      template: ActiveListen,
      high_social_desirability: ActiveListenHighSocialDesirability,
      id: "active_listen",
    },
    {
      template: AutoEfficient,
      high_social_desirability: AutoEfficientHighSocialDesirability,
      id: "auto_efficient",
    },
    { template: Planning, id: "planning" },
    { template: Decisions, id: "decisions" },
    {
      template: Empathy,
      high_social_desirability: EmpathyHighSocialDesirability,
      id: "empathy",
    },
    { template: TimeManage, id: "time_manage" },
    { template: LearnStrategy, id: "learn_strategy" },
    { template: Leadership, id: "leadership" },
  ];

  let documents_structure = [Start];

  // Add template for each skill evaluated
  skills_evaluated.value.forEach((skill) => {
    const id = skill.id_v2 ?? skill.id;

    const highSocialDesirability =
      props.candidate.results.social_desire_score?.score > 75;
    const skillTemplate = skills_template.find((elt) => elt.id === id);
    const template =
      highSocialDesirability && skillTemplate.high_social_desirability
        ? skillTemplate?.high_social_desirability
        : skillTemplate?.template;

    if (template) documents_structure.push(template);
  });

  if (campaignStore.currentCampaign.version === 2) {
    documents_structure.push(Motivation);
  }

  if (campaignStore.currentCampaign.version === 2) {
    documents_structure.push(Badges);
  }

  documents_structure.push(End);

  const promises = [];

  // Generate PDF for each template
  documents_structure.forEach((template, index) => {
    const promise = new Promise((resolve) => {
      generate({
        template,
        plugins,
        inputs: [{ ...inputs[0], index }],
        options: { font },
      }).then((pdf) => {
        resolve(pdf);
      });
    });

    promises.push(promise);
  });

  const pdfBuffers = await Promise.all(promises);

  // Création d'un document PDF vide pour la fusion
  const mergedPdf = await PDFDocument.create();

  // Fusion de chaque PDF
  for (const pdfBuffer of pdfBuffers) {
    const pdfToMerge = await PDFDocument.load(pdfBuffer.buffer);
    const copiedPages = await mergedPdf.copyPages(
      pdfToMerge,
      pdfToMerge.getPageIndices()
    );
    copiedPages.forEach((page) => mergedPdf.addPage(page));
  }

  // Génération du PDF fusionné
  const mergedPdfBytes = await mergedPdf.save();

  const blob = new Blob([mergedPdfBytes], { type: "application/pdf" });

  state.document_url = URL.createObjectURL(blob);

  // Start download
  setTimeout(() => {
    download_link.value.click();
    window.URL.revokeObjectURL(state.document_url);
    state.is_loading = false;
    state.is_ready = true;
    state.download_button_label = "Télécharger le rapport";
  }, 100);
}

watch(props, async (props) => {
  if (!props.isOpen) {
    state.is_ready = false;
    return;
  }

  state.is_loading = true;

  setTimeout(async () => {
    await buildInputObject();

    state.is_ready = true;
    state.is_loading = false;
  }, 300);
});

// Build input object for PDF generation (All data needed to complete the PDF: text, images...)
async function buildInputObject() {
  const images_data = {};

  // Get all images to convert
  Array.from(document.querySelectorAll("[data-convert]")).forEach((img) => {
    images_data[img.getAttribute("data-convert")] = img;
  });

  // Get global data
  state.inputs = [
    {
      total_pages: nb_pages.value,
      company_logo: await convertElementToData(images_data.company_logo),
      name: `${props.candidate.candidate.first_name} ${props.candidate.candidate.last_name}`.toUpperCase(),
      campaign_name: `Campagne associée : ${campaignStore.currentCampaign.name}`,
      date: `Rapport exporté le : ${getDate(new Date(), "dddd D MMMM YYYY")}`,
      compatibility: await convertElementToData(images_data.compatibility),
      result_tags: await convertElementToData(images_data.result_tags),
      position: await convertElementToData(images_data.position),
      candidate_profile: await convertElementToData(
        images_data.candidate_profile
      ),
    },
  ];

  if (campaignStore.currentCampaign.version === 2) {
    state.inputs[0].badges = await convertElementToData(images_data.badges);

    const motivations = modules.find((elt) => elt.id === "motivation");

    const motivationTextValues =
      motivations?.result_wordings?.[props.candidate.results.motivation_style];
    Object.assign(state.inputs[0], {
      motivation_analysis: motivationTextValues.analysis,
      motivation_advantages: motivationTextValues.plus,
      motivation_disadvantages: motivationTextValues.minus,
      motivation_question_number_1: motivationTextValues.questions[0],
      motivation_question_number_2: motivationTextValues.questions[1],
      motivation_question_number_3: motivationTextValues.questions[2],
    });
  }

  const promises = [];

  // Get all skills data and put them into the input object
  skills_evaluated.value.forEach((skill) => {
    const isV2 = campaignStore.currentCampaign.version === 2;

    const resultSkillId = isV2
      ? Object.keys(props.candidate.results).find(
          (mod) => mod.includes(skill.id_v2) || mod.includes(skill.id)
        )
      : skill.id;

    let rank = props.candidate.results[resultSkillId].rank;
    if (skill.id === "leadership") {
      const candidateStrategies = JSON.parse(props.candidate.results[resultSkillId].value);
      rank = Object.keys(candidateStrategies.categories).reduce((a, b) => {
        return candidateStrategies.categories[a] > candidateStrategies.categories[b] ? a : b;
      });
    }
    const order = props.candidate.results[resultSkillId]?.badge_rank;
    const appreciation =
      skill.type !== "style"
        ? getAppreciation(resultSkillId)
        : getResultWording(skill, rank, "analysis");

    const badge_labels = ["Or", "Argent", "Bronze"];

    const promise = async () => {
      let properties = {
        [`${skill.id}_score`]: await convertElementToData(
          images_data[`${resultSkillId}_circular`]
        ),
        [`${skill.id}_appreciation`]: appreciation,
        [`${skill.id}_analysis`]: getResultWording(skill, rank, "analysis"),
        [`${skill.id}_analysis_teamwork`]: getResultWording(skill, rank, "analysis_teamwork"),
        [`${skill.id}_analysis_communication`]: getResultWording(skill, rank, "analysis_communication"),
        [`${skill.id}_plus`]: getAdvantages(skill, rank, "plus"),
        [`${skill.id}_advices`]: getResultWording(skill, rank, "advices"),
        [`${skill.id}_question_number_1`]: getQuestion(skill, rank, 0),
        [`${skill.id}_question_number_2`]: getQuestion(skill, rank, 1),
        [`${skill.id}_question_number_3`]: getQuestion(skill, rank, 2),
      };

      // In v2 we don't have badges
      if (!isV2) {
        properties = {
          ...properties,
          [`${skill.id}_badge`]: await convertElementToData(
            images_data[`${skill.id}_badge`]
          ),
          [`${skill.id}_badge_svg`]: document.querySelector(
            `[data-convert='${skill.id}_badge'] .badge-inner`
          ).innerHTML,
          [`${skill.id}_badge_name`]: `Badge ${badge_labels[order - 1]}`,
          [`${skill.id}_badge_title`]: `Compétence N°${order}`,
          [`${skill.id}_badge_description`]: `Pour cette compétence, voici le badge obtenu par ce profil sur son rapport de participant. Vous pouvez le mentionner lors de vos échanges.`,
        };
      }

      Object.assign(state.inputs[0], properties);
    };

    promises.push(promise);
  });

  await Promise.all(promises.map((asyncFn) => asyncFn()));
}

// Convert HTML element to base64 image
async function convertElementToData(element) {
  return await html2canvas(element, {
    backgroundColor: null,
    useCORS: true,
    allowTaint: false,
  }).then((v) => v.toDataURL());
}

function getResultWording(skill, rank, property) {
  let wording = skill.result_wordings[rank][property];

  return wording;
}

function getAdvantages(skill, rank, property) {
  const advantages = getResultWording(skill, rank, property);

  if (Array.isArray(advantages)) return advantages.map((elt) => `• ${elt.text}`).join("\n\n");

  return advantages;
}

function getQuestion(skill, rank, number) {
  const questions = getResultWording(skill, rank, "questions");

  return questions?.length > 0 ? questions[number] : null;
}

function getAppreciation(module) {
  const moduleObj = modules.find(
    (elt) => elt.id === module || elt.id_v2 === module
  );
  if (moduleObj?.type === "style") {
    return getResultWording(moduleObj, "analysis");
  }

  return getWording(
    `campaign.results.appreciations['${props.candidate.results[module].rank}']`
  );
}

function getResultSkillId(skill) {
  if (campaignStore.currentCampaign.version === 1) return skill.id;

  return Object.keys(props.candidate.results).find((key) =>
    key.includes(skill.id_v2 ?? skill.id)
  );
}
</script>
